import { ENV } from "config/configUrl";

const production = (ENV as string) === "production";

// A date and time as the admin shows them (28/09/2026 20:46:38); "" when absent.
export const formatDateTime = (value?: string | number | Date) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" });
};

// A crypto amount without rounding away its small units (0.000435 BTC stays so).
export const formatCrypto = (amount?: number | string | null) => {
  if (amount === undefined || amount === null || amount === "") return "";
  const value = Number(amount);
  if (!Number.isFinite(value)) return String(amount);
  return value.toLocaleString("en-US", { maximumFractionDigits: 8, useGrouping: false });
};

export const formatEuro = (amount?: number | string | null) =>
  amount === undefined || amount === null || amount === "" ? "" : `${amount} €`;

// The explorer page of a transaction, by the currency it moved.
const EXPLORERS: Record<string, (tx: string) => string> = {
  BTC: (tx) => (production ? `https://mempool.space/tx/${tx}` : `https://mempool.space/signet/tx/${tx}`),
  SOL: (tx) => (production ? `https://solscan.io/tx/${tx}` : `https://solscan.io/tx/${tx}?cluster=devnet`),
  POL: (tx) => (production ? `https://polygonscan.com/tx/${tx}` : `https://amoy.polygonscan.com/tx/${tx}`),
  AVAX: (tx) => (production ? `https://snowtrace.io/tx/${tx}` : `https://testnet.snowtrace.io/tx/${tx}`),
};
EXPLORERS.MATIC = EXPLORERS.POL;
const ethereumExplorer = (tx: string) => (production ? `https://etherscan.io/tx/${tx}` : `https://sepolia.etherscan.io/tx/${tx}`);

// An on-chain transaction id: an EVM or Bitcoin hash, or a Solana signature. Internal
// references (rf-...) have no explorer page.
const CHAIN_TX = /^(0x)?[0-9a-fA-F]{64}$|^[1-9A-HJ-NP-Za-km-z]{80,90}$/;

export const explorerTxUrl = (currency: string | undefined, tx?: string) =>
  tx && CHAIN_TX.test(tx) ? (EXPLORERS[String(currency || "").toUpperCase()] || ethereumExplorer)(tx) : null;

// A date alone (31/12/2027), for day-level dates such as a link's expiry.
export const formatDate = (value?: string | number | Date) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("fr-FR", { timeZone: "UTC" });
};

// A network as payers and merchants read it; its code when unknown.
const NETWORK_LABELS: Record<string, string> = {
  Ethereum: "Ethereum",
  EthereumSepolia: "Ethereum Sepolia",
  EthereumHolesky: "Ethereum Holesky",
  Bitcoin: "Bitcoin",
  BitcoinSignet: "Bitcoin Signet",
  BitcoinTestnet3: "Bitcoin Testnet",
  Solana: "Solana",
  SolanaDevnet: "Solana Devnet",
  Polygon: "Polygon",
  PolygonAmoy: "Polygon Amoy",
  AvalancheC: "Avalanche C-Chain",
  AvalancheCFuji: "Avalanche Fuji",
  ArbitrumOne: "Arbitrum One",
  ArbitrumSepolia: "Arbitrum Sepolia",
  Base: "Base",
  BaseSepolia: "Base Sepolia",
  Optimism: "Optimism",
  OptimismSepolia: "Optimism Sepolia",
};
export const networkLabel = (network?: string) => (network ? NETWORK_LABELS[network] || network : "");

// A payment link's amount: in euros, or in the crypto it is priced in.
export const linkAmount = (params: { euroAmount?: number; amount?: number | string; currency?: string } = {}) =>
  params.euroAmount !== undefined && params.euroAmount !== null
    ? formatEuro(params.euroAmount)
    : params.amount !== undefined && params.amount !== null
    ? `${formatCrypto(params.amount)} ${String(params.currency || "").toUpperCase()}`
    : "N/A";
